import React from "react";
import { VISIBLE_STAGES, PRIORITIES, CYCLE_TIME_AVG } from "../constants/config";
import {
  SURFACE,
  BORDER,
  TEXT,
  ACCENT,
  CATEGORY_PALETTE,
} from "../constants/colors";
import { formatCurrency } from "../utils/formatCurrency";

// ── Sub-components for each metric panel ────────────────────────────

const PanelHeader = ({ children }) => (
  <div
    style={{
      fontSize: 12,
      fontWeight: 700,
      color: TEXT.secondary,
      marginBottom: 12,
      textTransform: "uppercase",
      letterSpacing: 1,
    }}
  >
    {children}
  </div>
);

const StageDistribution = ({ cards }) => (
  <div
    style={{
      background: SURFACE.panelBg,
      border: `1px solid ${BORDER.subtle}`,
      borderRadius: 10,
      // padding: 16,
    }}
  >
    <PanelHeader>Stage Distribution</PanelHeader>
    {VISIBLE_STAGES.map((s) => {
      const cnt = cards.filter((c) => c.stage === s.id).length;
      const pct = cards.length ? (cnt / cards.length) * 100 : 0;
      return (
        <div key={s.id} style={{ marginBottom: 10 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 4,
            }}
          >
            <span style={{ fontSize: 11, color: TEXT.tertiary }}>
              {s.label}
            </span>
            <span
              style={{
                fontSize: 11,
                fontFamily: "Poppins",
                color: s.color,
                fontWeight: 700,
              }}
            >
              {cnt}
            </span>
          </div>
          <div
            style={{ height: 4, background: SURFACE.barBg, borderRadius: 2 }}
          >
            <div
              style={{
                height: "100%",
                width: `${pct}%`,
                background: s.color,
                borderRadius: 2,
              }}
            />
          </div>
        </div>
      );
    })}
  </div>
);

const PriorityBreakdown = ({ cards }) => (
  <div
    style={{
      background: SURFACE.panelBg,
      border: `1px solid ${BORDER.subtle}`,
      borderRadius: 10,
      // padding: 16,
    }}
  >
    <PanelHeader>Priority Breakdown</PanelHeader>
    {Object.entries(PRIORITIES).map(([key, p]) => {
      const cnt = cards.filter((c) => c.priority === key).length;
      const val = cards
        .filter((c) => c.priority === key)
        .reduce((s, c) => s + c.value, 0);
      return (
        <div
          key={key}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
            padding: "8px 10px",
            background: SURFACE.barBg,
            borderRadius: 6,
            borderLeft: `3px solid ${p.color}`,
          }}
        >
          <div>
            <span
              style={{
                fontSize: 12,
                color: p.color,
                fontFamily: "Poppins",
                fontWeight: 700,
                marginRight: 8,
              }}
            >
              {p.label}
            </span>
            <span style={{ fontSize: 11, color: TEXT.tertiary }}>
              {cnt} items
            </span>
          </div>
          <span
            style={{
              fontSize: 12,
              color: ACCENT.green,
              fontFamily: "Poppins",
              fontWeight: 700,
            }}
          >
            {formatCurrency(val)}
          </span>
        </div>
      );
    })}
  </div>
);

const ValueByCategory = ({ cards, categories, totalValue }) => (
  <div
    style={{
      background: SURFACE.panelBg,
      border: `1px solid ${BORDER.subtle}`,
      borderRadius: 10,
      // padding: 16,
    }}
  >
    <PanelHeader>Value by Category</PanelHeader>
    {categories.map((cat, i) => {
      const val = cards
        .filter((c) => c.category === cat)
        .reduce((s, c) => s + c.value, 0);
      const pct = (val / totalValue) * 100;
      const color = CATEGORY_PALETTE[i % CATEGORY_PALETTE.length];
      return (
        <div key={cat} style={{ marginBottom: 10 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 3,
            }}
          >
            <span style={{ fontSize: 11, color: TEXT.tertiary }}>{cat}</span>
            <span
              style={{
                fontSize: 11,
                fontFamily: "Poppins",
                color,
                fontWeight: 700,
              }}
            >
              {formatCurrency(val)}
            </span>
          </div>
          <div
            style={{ height: 4, background: SURFACE.barBg, borderRadius: 2 }}
          >
            <div
              style={{
                height: "100%",
                width: `${pct}%`,
                background: color,
                borderRadius: 2,
              }}
            />
          </div>
        </div>
      );
    })}
  </div>
);

const SupplierDependency = ({ cards }) => {
  const suppliers = [...new Set(cards.map((c) => c.supplier))];
  return (
    <div
      style={{
        background: SURFACE.panelBg,
        border: `1px solid ${BORDER.subtle}`,
        borderRadius: 10,
        // padding: 16,
        gridColumn: "span 2",
      }}
    >
      <PanelHeader>Supplier Dependency Map</PanelHeader>
      {suppliers.map((sup) => {
        const supCards = cards.filter((c) => c.supplier === sup);
        const val = supCards.reduce((s, c) => s + c.value, 0);
        const hasBlocked = supCards.some((c) => c.status === "blocked");
        const hasRisk = supCards.some((c) => c.status === "at_risk");
        return (
          <div
            key={sup}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
              padding: "8px 12px",
              background: SURFACE.barBg,
              borderRadius: 6,
              border: `1px solid ${hasBlocked ? `${ACCENT.red}30` : hasRisk ? `${ACCENT.amber}20` : BORDER.subtle}`,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {hasBlocked && (
                <span style={{ fontSize: 10, color: ACCENT.red }}>■</span>
              )}
              {!hasBlocked && hasRisk && (
                <span style={{ fontSize: 10, color: ACCENT.amber }}>◆</span>
              )}
              {!hasBlocked && !hasRisk && (
                <span style={{ fontSize: 10, color: ACCENT.green }}>▲</span>
              )}
              <span
                style={{ fontSize: 12, color: TEXT.secondary, fontWeight: 600 }}
              >
                {sup}
              </span>
              <span style={{ fontSize: 11, color: TEXT.muted }}>
                {supCards.length} POs
              </span>
            </div>
            <span
              style={{
                fontSize: 12,
                fontFamily: "DM Mono, monospace",
                color: ACCENT.green,
                fontWeight: 700,
              }}
            >
              {formatCurrency(val)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const CycleTimePanel = ({ cards }) => (
  <div
    style={{
      background: SURFACE.panelBg,
      border: `1px solid ${BORDER.subtle}`,
      borderRadius: 10,
      // padding: 16,
    }}
  >
    <PanelHeader>Avg Cycle Time (days)</PanelHeader>
    {VISIBLE_STAGES.filter((s) => CYCLE_TIME_AVG[s.id] > 0).map((s) => (
      <div key={s.id} style={{ marginBottom: 10 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 3,
          }}
        >
          <span style={{ fontSize: 11, color: TEXT.tertiary }}>{s.label}</span>
          <span
            style={{
              fontSize: 11,
              fontFamily: "DM Mono, monospace",
              color: s.color,
              fontWeight: 700,
            }}
          >
            {CYCLE_TIME_AVG[s.id]}d
          </span>
        </div>
        <div style={{ height: 4, background: SURFACE.barBg, borderRadius: 2 }}>
          <div
            style={{
              height: "100%",
              width: `${(CYCLE_TIME_AVG[s.id] / 14) * 100}%`,
              background: s.color,
              borderRadius: 2,
            }}
          />
        </div>
      </div>
    ))}
  </div>
);

// ── Main Metrics View ───────────────────────────────────────────────

const MetricsView = ({ cards }) => {
  const totalValue = cards.reduce((s, c) => s + c.value, 0);
  const categories = [...new Set(cards.map((c) => c.category))];

  return (
    <div
      style={{
        flex: 1,
        padding: 24,
        overflowY: "auto",
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: 16,
      }}
    >
      <StageDistribution cards={cards} />
      <PriorityBreakdown cards={cards} />
      <ValueByCategory
        cards={cards}
        categories={categories}
        totalValue={totalValue}
      />
      <SupplierDependency cards={cards} />
      <CycleTimePanel cards={cards} />
    </div>
  );
};

export default MetricsView;
